import { useCallback, useEffect, useRef, useState } from 'react';

import { Check, Copy } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import type { ToastT } from 'sonner';

import { Button } from '@/shadcn/button';
import { cn } from '@/shadcn/lib/utils';

type Props = {
  type?: ToastT['type'];
  title: string;
} & (
  | {
      description: string;
      multiDescription?: never;
    }
  | {
      multiDescription: (string | undefined)[];
      description?: never;
    }
);

const COPY_RESET_MS = 2000;
const ICON_EXIT_ROTATE_DEG = 12;
const ICON_EXIT_SCALE = 0.6;
const ICON_STROKE_CHECK = 2.25;
const ICON_STROKE_COPY = 2;

const iconTransition = {
  duration: 0.18,
  ease: 'easeOut'
} as const;

export const CustomToast = (props: Props) => {
  let description: (typeof props)['description'];
  let multiDescription: (typeof props)['multiDescription'];

  if ('multiDescription' in props && props.multiDescription) {
    if (props.multiDescription.length > 1) {
      multiDescription = props.multiDescription;
    } else if (props.multiDescription.length === 1) {
      description = props.multiDescription[0];
    }
  } else {
    description = props.description;
  }

  const copyText =
    multiDescription !== undefined
      ? multiDescription.filter(Boolean).join('\n')
      : (description ?? '');

  const hasCopyableText = copyText.length > 0;

  const [copied, setCopied] = useState(false);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();

  useEffect(
    () => () => {
      if (resetTimeoutRef.current !== undefined) {
        clearTimeout(resetTimeoutRef.current);
      }
    },
    []
  );

  const handleCopy = useCallback(async () => {
    if (!hasCopyableText) {
      return;
    }
    try {
      setCopied(true);
      if (resetTimeoutRef.current !== undefined) {
        clearTimeout(resetTimeoutRef.current);
      }
      resetTimeoutRef.current = setTimeout(() => {
        setCopied(false);
      }, COPY_RESET_MS);
      await navigator.clipboard.writeText(copyText);
    } catch {
      console.error('Cannot copy text to clipboard when in http mode.');
    }
  }, [copyText, hasCopyableText]);

  return (
    <div
      className={cn(
        'relative ml-2 flex select-none flex-col font-normal',
        hasCopyableText && 'pr-8'
      )}
      data-cy="custom-toast"
    >
      {hasCopyableText ? (
        <Button
          aria-label={
            copied ? 'Copied to clipboard' : 'Copy message to clipboard'
          }
          className="absolute top-0 right-0 hover:bg-transparent hover:text-destructive"
          data-cy="custom-toast-copy"
          onClick={handleCopy}
          size="smallIcon"
          type="button"
          variant="ghost"
        >
          <span className="relative flex size-4 items-center justify-center">
            <AnimatePresence initial={false} mode="popLayout">
              {copied ? (
                <motion.span
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  className="absolute inline-flex text-emerald-600 dark:text-emerald-400"
                  exit={{
                    opacity: 0,
                    rotate: -ICON_EXIT_ROTATE_DEG,
                    scale: ICON_EXIT_SCALE
                  }}
                  initial={{
                    opacity: 0,
                    rotate: ICON_EXIT_ROTATE_DEG,
                    scale: ICON_EXIT_SCALE
                  }}
                  key="copied"
                  transition={iconTransition}
                >
                  <Check
                    aria-hidden
                    className="size-4"
                    strokeWidth={ICON_STROKE_CHECK}
                  />
                </motion.span>
              ) : (
                <motion.span
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  className="absolute inline-flex"
                  exit={{
                    opacity: 0,
                    rotate: ICON_EXIT_ROTATE_DEG,
                    scale: ICON_EXIT_SCALE
                  }}
                  initial={{
                    opacity: 0,
                    rotate: -ICON_EXIT_ROTATE_DEG,
                    scale: ICON_EXIT_SCALE
                  }}
                  key="copy"
                  transition={iconTransition}
                >
                  <Copy
                    aria-hidden
                    className="size-4"
                    strokeWidth={ICON_STROKE_COPY}
                  />
                </motion.span>
              )}
            </AnimatePresence>
          </span>
        </Button>
      ) : null}
      <div className="font-medium leading-[1.5]" data-cy="custom-toast-title">
        {props.title}
      </div>
      {description && (
        <div
          className="overflow-y-hidden text-ellipsis leading-[1.4] first-letter:uppercase"
          data-cy="custom-toast-description"
        >
          {description}
        </div>
      )}
      {multiDescription && (
        <ul className="list-disc pl-4" data-cy="custom-toast-multi-description">
          {multiDescription.map((desc, index) =>
            desc ? (
              <li className="leading-[1.4] first-letter:uppercase" key={index}>
                {desc}
              </li>
            ) : null
          )}
        </ul>
      )}
    </div>
  );
};
